import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Loader2, Trash2 } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";
import BookingActionButtons from "./BookingActionButtons";

export default function BookingEditModal({
  open,
  onOpenChange,
  booking,
  onSaved,
  onDeleted,
}) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Track the latest version of this booking locally so quick actions
  // (Confirm / Complete / Reopen / etc.) re-render the modal immediately.
  const [currentBooking, setCurrentBooking] = useState(booking);
  useEffect(() => {
    setCurrentBooking(booking);
  }, [booking]);

  useEffect(() => {
    if (open && booking) {
      setForm({
        name: booking.name || "",
        phone: booking.phone || "",
        email: booking.email || "",
        address: booking.address || "",
        suburb: booking.suburb || "",
        service_title: booking.service_title || "",
        notes: booking.notes || "",
        date: booking.date || "",
        time: booking.time || "",
        time_to: booking.time_to || "",
      });
    }
  }, [open, booking]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!currentBooking) return;
    setSubmitting(true);
    try {
      const r = await authAxios.patch(`/bookings/${currentBooking.id}`, form);
      toast({ title: "Booking updated" });
      setCurrentBooking(r.data);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Save failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    if (!currentBooking) return;
    setDeleting(true);
    try {
      await authAxios.delete(`/bookings/${currentBooking.id}`);
      toast({ title: "Booking deleted" });
      onDeleted?.(currentBooking.id);
      setConfirmDeleteOpen(false);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Delete failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit booking</DialogTitle>
            <DialogDescription>
              {currentBooking ? `${currentBooking.reference} · ${currentBooking.status}` : ""}
            </DialogDescription>
          </DialogHeader>

          {currentBooking && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 font-semibold">
                  Booking actions
                </div>
                <BookingActionButtons
                  booking={currentBooking}
                  onUpdated={(updated) => {
                    setCurrentBooking(updated);
                    onSaved?.(updated);
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Customer name">
                  <Input
                    value={form.name || ""}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid="edit-name"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    value={form.phone || ""}
                    onChange={(e) => update("phone", e.target.value)}
                    data-testid="edit-phone"
                  />
                </Field>
                <Field label="Email" colSpan={2}>
                  <Input
                    type="email"
                    value={form.email || ""}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid="edit-email"
                  />
                </Field>
                <Field label="Address">
                  <Input
                    value={form.address || ""}
                    onChange={(e) => update("address", e.target.value)}
                    data-testid="edit-address"
                  />
                </Field>
                <Field label="Suburb">
                  <Input
                    value={form.suburb || ""}
                    onChange={(e) => update("suburb", e.target.value)}
                    data-testid="edit-suburb"
                  />
                </Field>
                <Field label="Service" colSpan={2}>
                  <Input
                    value={form.service_title || ""}
                    onChange={(e) => update("service_title", e.target.value)}
                    data-testid="edit-service-title"
                  />
                </Field>
                <Field label="Date">
                  <Input
                    type="date"
                    value={form.date || ""}
                    onChange={(e) => update("date", e.target.value)}
                    data-testid="edit-date"
                  />
                </Field>
                <div />
                <Field label="From">
                  <Input
                    type="time"
                    value={form.time || ""}
                    onChange={(e) => update("time", e.target.value)}
                    data-testid="edit-time-from"
                  />
                </Field>
                <Field label="To">
                  <Input
                    type="time"
                    value={form.time_to || ""}
                    onChange={(e) => update("time_to", e.target.value)}
                    data-testid="edit-time-to"
                  />
                </Field>
                <Field label="Notes" colSpan={2}>
                  <Textarea
                    rows={3}
                    value={form.notes || ""}
                    onChange={(e) => update("notes", e.target.value)}
                    data-testid="edit-notes"
                  />
                </Field>
              </div>
              <p className="text-xs text-slate-500">
                Editing here does not send any SMS to the customer. To notify them, use Confirm or Reschedule from the booking actions.
              </p>
            </div>
          )}

          <DialogFooter className="flex flex-row justify-between sm:justify-between">
            <Button
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={submitting || deleting}
              data-testid="edit-delete-btn"
            >
              <Trash2 size={14} className="mr-1" /> Delete
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={submitting}
                className="bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="edit-save-btn"
              >
                {submitting && <Loader2 className="animate-spin mr-2" size={14} />}
                Save changes
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {currentBooking ? `${currentBooking.reference} · ${currentBooking.name} · ${currentBooking.date}` : ""}
              <br />
              This permanently removes the booking record. The customer is NOT notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-btn"
            >
              {deleting && <Loader2 className="animate-spin mr-2" size={14} />}
              Delete booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function Field({ label, colSpan = 1, children }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-xs text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
